"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.ConnecticutRules = void 0;
const wasteAccumulation_1 = require("encamp-shared/dist/constants/wasteAccumulation");
const wasteGeneratorStatusThresholds_1 = require("encamp-shared/dist/constants/wasteGeneratorStatusThresholds");
const unwrap_1 = require("../../unwrap");
const onSiteAccumulationThresholds_1 = require("../onSiteAccumulationThresholds");
const FEDERAL_1 = require("./FEDERAL");
const CT_DERIVATIONS = {
    effectiveWasteKind: {
        from: ["wasteKind", "managedAsUniversalWaste"],
        label: "Connecticut effective subclassification",
        citation: "RCSA § 22a-449(c)-100 (40 CFR 261.9, July 1, 2000 ed.)",
        description: (0, unwrap_1.unwrap) `Whether waste is universal waste depends on whether Connecticut adopted
      the category; that determination is made upstream and arrives as managedAsUniversalWaste.
      Connecticut has not adopted 40 CFR 266 subpart P, so pharmaceutical waste is fully
      regulated hazardous waste on the ordinary standards regardless of the facility's
      subpart P flag.`,
        cases: [
            {
                when: { wasteKind: "UNIVERSAL_WASTE", managedAsUniversalWaste: true },
                produces: "UNIVERSAL_WASTE",
            },
            {
                when: { wasteKind: "UNIVERSAL_WASTE", managedAsUniversalWaste: false },
                produces: "ORDINARY",
            },
            { when: { wasteKind: "USED_OIL" }, produces: "USED_OIL" },
            { when: { wasteKind: "PHARMACEUTICAL" }, produces: "ORDINARY" },
            { when: { wasteKind: null }, produces: "ORDINARY" },
        ],
    },
};
/**
 * Spec, not snapshot (see the discipline header in ../types.ts): a row
 * changes only when a regulatory specialist says the law reads differently —
 * never to make the class below pass.
 */
const CT_LQG_CLOCK = {
    kind: "TIME",
    days: 90,
    citation: "RCSA § 22a-449(c)-102 (40 CFR 262.34(a), July 1, 2000 ed.)",
    jurisdiction: "CT",
};
const CT_SQG_CLOCK = {
    kind: "TIME",
    days: 180,
    citation: "RCSA § 22a-449(c)-102 (40 CFR 262.34(d), July 1, 2000 ed.)",
    jurisdiction: "CT",
};
const CT_CESQG_CITATION = "RCSA § 22a-449(c)-101 (40 CFR 261.5, July 1, 2000 ed.)";
const CT_CESQG_NO_CLOCK = {
    kind: "NO_LIMIT",
    citation: CT_CESQG_CITATION,
    jurisdiction: "CT",
};
const CT_CESQG_NO_CAPS = {
    kind: "NO_LIMIT",
    citation: CT_CESQG_CITATION,
    jurisdiction: "CT",
};
const CT_SATELLITE_CITATION = "RCSA § 22a-449(c)-102 (40 CFR 262.34(c), July 1, 2000 ed.)";
const CT_SATELLITE_CAPS = {
    kind: "VOLUME",
    nonAcuteGal: wasteAccumulation_1.SAA_NON_ACUTE_VOLUME_LIMIT_GAL,
    acuteGal: wasteAccumulation_1.SAA_ACUTE_VOLUME_LIMIT_GAL,
    acuteKg: wasteAccumulation_1.SAA_ACUTE_MASS_LIMIT_KG,
    moveWindowDays: wasteAccumulation_1.SATELLITE_MOVE_WINDOW_DAYS,
    citation: CT_SATELLITE_CITATION,
    jurisdiction: "CT",
};
const CT_NO_STANDING_SATELLITE_CLOCK = {
    kind: "NO_LIMIT",
    citation: CT_SATELLITE_CITATION,
    jurisdiction: "CT",
};
const CT_SQG_NOTE = (0, unwrap_1.unwrap) `
  Connecticut incorporates 40 CFR 262.34(d) as of July 1, 2000, which on its face carries the
  270-day option for waste transported over 200 miles — but DEEP guidance states 180 days only,
  and the 270-day adoption is a textual inference. Held at 180 days on both values of the
  200-mile flag, the more protective reading, until DEEP confirms.`;
const CT_CESQG_CENTRAL_NOTE = (0, unwrap_1.unwrap) `
  Connecticut's pre-GIR name for the federal VSQG tier is the conditionally exempt small quantity
  generator (CESQG); this row is keyed on the federal status because Connecticut defines no tier
  of its own and RCRAInfo carries none for it. A CESQG under 40 CFR 261.5 (as incorporated) is
  not subject to 262.34's accumulation clocks. What constrains it is the 1,000 kg non-acute /
  1 kg acute on-site accumulation threshold, which has its own table below. Connecticut's modification is that a
  CESQG exceeding 1,000 kg becomes an LQG, not an SQG; that escalation is surfaced through the
  over-threshold finding rather than applied to the stored status. Only the on-site threshold judgment
  carries the CESQG name; the clock here is keyed and named on the federal axis.`;
const CT_SATELLITE_NOTE = (0, unwrap_1.unwrap) `
  The same 55 gallon / 1 quart / 3 day figures as federal law, under Connecticut's own
  incorporation of 262.34(c). Connecticut adds content-identity marking ("Flammable", "Acid",
  "Alkaline", "Cyanide") and bars satellite-to-satellite moves; neither is modeled by this
  engine. The 1 kg acute mass cap is the federal stand-in for the one-quart limit — over-strict,
  never under-strict.`;
const CT_CESQG_SATELLITE_NOTE = (0, unwrap_1.unwrap) `
  Mirrors the federal reading that the satellite provision is relief from central clocks a
  CESQG never had, so the caps do not reach one. Unconfirmed by a regulatory specialist.`;
const CT_UNIVERSAL_WASTE_NOTE = (0, unwrap_1.unwrap) `
  Connecticut adopts 40 CFR part 273 by reference, so the federal answers are inherited and
  federal citations shown; the incorporating RCSA section is for the SME to supply. Which
  categories Connecticut adopted is decided upstream and arrives as managedAsUniversalWaste.`;
const CT_USED_OIL_NOTE = (0, unwrap_1.unwrap) `
  Connecticut's used-oil treatment is not covered by the state comparison this class was
  drafted from, so the federal part 279 answer is inherited exactly as an unseeded state's
  would be; a Connecticut-specific citation is left for the SME.`;
const CT_EXPECTATIONS = [
    // ── CENTRAL ACCUMULATION — RCSA § 22a-449(c)-102 (40 CFR 262.34) ──────
    // Keyed on the federal status alone — see the class header.
    {
        given: { effectiveWasteKind: "ORDINARY" },
        when: { areaType: "CENTRAL", generatorStatus: "LQG" },
        expect: { clock: CT_LQG_CLOCK },
    },
    {
        given: { effectiveWasteKind: "ORDINARY" },
        // Spans mustTransport200Miles deliberately — see the note.
        when: { areaType: "CENTRAL", generatorStatus: "SQG" },
        expect: { clock: CT_SQG_CLOCK },
        note: CT_SQG_NOTE,
    },
    {
        given: { effectiveWasteKind: "ORDINARY" },
        when: { areaType: "CENTRAL", generatorStatus: "VSQG" },
        expect: { clock: CT_CESQG_NO_CLOCK },
        note: CT_CESQG_CENTRAL_NOTE,
    },
    {
        given: { effectiveWasteKind: "ORDINARY" },
        when: { areaType: "CENTRAL", generatorStatus: null },
        expect: { clock: { kind: "UNDETERMINED" } },
    },
    // ── SATELLITE ACCUMULATION — RCSA § 22a-449(c)-102 (40 CFR 262.34(c)) ─
    {
        given: { effectiveWasteKind: "ORDINARY" },
        when: { areaType: "SATELLITE", generatorStatus: "LQG" },
        expect: { caps: CT_SATELLITE_CAPS, clock: CT_NO_STANDING_SATELLITE_CLOCK },
        note: CT_SATELLITE_NOTE,
    },
    {
        given: { effectiveWasteKind: "ORDINARY" },
        when: { areaType: "SATELLITE", generatorStatus: "SQG" },
        expect: { caps: CT_SATELLITE_CAPS, clock: CT_NO_STANDING_SATELLITE_CLOCK },
        note: CT_SATELLITE_NOTE,
    },
    {
        given: { effectiveWasteKind: "ORDINARY" },
        when: { areaType: "SATELLITE", generatorStatus: "VSQG" },
        expect: { caps: CT_CESQG_NO_CAPS, clock: CT_NO_STANDING_SATELLITE_CLOCK },
        note: CT_CESQG_SATELLITE_NOTE,
    },
    {
        given: { effectiveWasteKind: "ORDINARY" },
        when: { areaType: "SATELLITE", generatorStatus: null },
        expect: { caps: CT_SATELLITE_CAPS, clock: CT_NO_STANDING_SATELLITE_CLOCK },
        note: "The more protective default until the status is resolved.",
    },
    // ── UNIVERSAL WASTE — adopted by reference ─────────────────────────────
    {
        given: { effectiveWasteKind: "UNIVERSAL_WASTE" },
        when: { areaType: "CENTRAL" },
        expect: {
            clock: {
                kind: "TIME",
                days: 365,
                citation: "40 CFR 273.15(a), 273.35(a)",
                jurisdiction: "FEDERAL",
            },
        },
        note: CT_UNIVERSAL_WASTE_NOTE,
    },
    {
        given: { effectiveWasteKind: "UNIVERSAL_WASTE" },
        when: { areaType: "SATELLITE" },
        expect: {
            caps: { kind: "NO_LIMIT", citation: "40 CFR 261.9, 273.1(b)", jurisdiction: "FEDERAL" },
            clock: {
                kind: "TIME",
                days: 365,
                citation: "40 CFR 273.15(a), 273.35(a)",
                jurisdiction: "FEDERAL",
            },
        },
        note: CT_UNIVERSAL_WASTE_NOTE,
    },
    // ── USED OIL — federal part 279 inherited ──────────────────────────────
    {
        given: { effectiveWasteKind: "USED_OIL" },
        when: { areaType: "CENTRAL" },
        expect: { clock: { kind: "NO_LIMIT", citation: "40 CFR 279.22", jurisdiction: "FEDERAL" } },
        note: CT_USED_OIL_NOTE,
    },
    {
        given: { effectiveWasteKind: "USED_OIL" },
        when: { areaType: "SATELLITE" },
        expect: {
            caps: { kind: "NO_LIMIT", citation: "40 CFR 261.6(a)(4)", jurisdiction: "FEDERAL" },
            clock: { kind: "NO_LIMIT", citation: "40 CFR 279.22", jurisdiction: "FEDERAL" },
        },
        note: CT_USED_OIL_NOTE,
    },
];
const CT_SQG_THRESHOLD_NOTE = (0, unwrap_1.unwrap) `
  RCSA § 22a-449(c)-102 modifies the incorporated 40 CFR 262.34(d) to hold an SQG to 1,000 kg
  on site — a SIXTH of the federal 6,000 kg — and, like the federal threshold, it is a single total
  across acute and non-acute waste. Awaits the SME gate.`;
const CT_CESQG_THRESHOLD_NOTE = (0, unwrap_1.unwrap) `
  40 CFR 261.5 as incorporated (RCSA § 22a-449(c)-101): 1,000 kg non-acute and 1 kg acute, limited
  separately — the same numbers the federal VSQG carries. Connecticut has not adopted the 2016
  Generator Improvements Rule, so the regulation still names this generator status the CESQG; the judgment
  keeps that name while behaving exactly as a federal VSQG. RCRAInfo offers Connecticut only a
  "same as federal" state status because the two statuses are functionally identical (SME-confirmed,
  2026-09-16). A facility exceeding 1,000 kg becomes an LQG (not an SQG) under Connecticut's
  modification; that escalation is surfaced through the over-threshold finding, not applied to the
  stored status.`;
/**
 * Spec, not snapshot. Keyed on the federal status alone, like the area rows
 * above — the state axis is never read for Connecticut. Named in the federal
 * vocabulary except for the smallest generator status, which Connecticut's regulation
 * still calls the CESQG (see the class header).
 */
const CT_ACCUMULATION_THRESHOLD_EXPECTATIONS = [
    {
        when: { generatorStatus: "LQG" },
        expect: {
            thresholds: { totalKg: null, nonAcuteKg: null, acuteKg: null },
            judgedAgainst: { abbreviation: "LQG", name: "Large Quantity Generator" },
        },
        note: "An LQG is time-limited (262.34(a) as incorporated); no accumulation threshold applies.",
    },
    {
        when: { generatorStatus: "SQG" },
        expect: {
            thresholds: { totalKg: 1000, nonAcuteKg: null, acuteKg: null },
            judgedAgainst: { abbreviation: "SQG", name: "Small Quantity Generator" },
        },
        note: CT_SQG_THRESHOLD_NOTE,
    },
    {
        when: { generatorStatus: "VSQG" },
        expect: {
            thresholds: { totalKg: null, nonAcuteKg: 1000, acuteKg: 1 },
            judgedAgainst: {
                abbreviation: "CESQG",
                name: "Conditionally Exempt Small Quantity Generator",
            },
        },
        note: CT_CESQG_THRESHOLD_NOTE,
    },
    {
        when: { generatorStatus: null },
        expect: { thresholds: { totalKg: null, nonAcuteKg: null, acuteKg: null }, judgedAgainst: null },
        note: "No federal status, so no thresholds: the caller prompts for setup.",
    },
];
/** Connecticut's pre-GIR name for the generator status the federal VSQG resolves to. */
const CT_CESQG_READABLE_STATUS = {
    abbreviation: "CESQG",
    name: "Conditionally Exempt Small Quantity Generator",
};
function getCTReadableGeneratorStatus(generatorStatus) {
    if (generatorStatus === null) {
        return null;
    }
    if (generatorStatus === "VSQG") {
        return CT_CESQG_READABLE_STATUS;
    }
    return FEDERAL_1.FEDERAL_READABLE_GENERATOR_STATUSES[generatorStatus];
}
function getCTAccumulationThresholds(generatorStatus) {
    switch (generatorStatus) {
        case "VSQG":
            return {
                totalKg: null,
                nonAcuteKg: wasteGeneratorStatusThresholds_1.VSQG_ACCUMULATION_THRESHOLD_KG,
                acuteKg: wasteGeneratorStatusThresholds_1.VSQG_ACCUMULATION_THRESHOLD_ACUTE_KG,
            };
        case "SQG":
            return { totalKg: wasteGeneratorStatusThresholds_1.CT_SQG_ACCUMULATION_THRESHOLD_KG, nonAcuteKg: null, acuteKg: null };
        default:
            return onSiteAccumulationThresholds_1.NO_ACCUMULATION_THRESHOLDS;
    }
}
function effectiveCtWasteKind(ctx) {
    if (ctx.wasteKind === "UNIVERSAL_WASTE" && ctx.managedAsUniversalWaste) {
        return "UNIVERSAL_WASTE";
    }
    if (ctx.wasteKind === "USED_OIL") {
        return "USED_OIL";
    }
    return "ORDINARY";
}
/**
 * Connecticut's clocks key on the federal tier alone, so that is the only
 * axis named as missing — the same shape the federal base returns.
 */
const CT_UNDETERMINED_CLOCK = {
    kind: "UNDETERMINED",
    missingAxes: ["generatorStatus"],
    reason: "Storage deadlines need this facility's generator status, which isn't known yet. " +
        "Generator status comes from the facility's EPA registration or its shipment history.",
};
/** Connecticut (DEEP) accumulation limits, RCSA § 22a-449(c)-100 to -102. */
class ConnecticutRules extends FEDERAL_1.FederalOnlyRules {
    constructor() {
        super(...arguments);
        this.expectations = CT_EXPECTATIONS;
        this.accumulationThresholdExpectations = CT_ACCUMULATION_THRESHOLD_EXPECTATIONS;
        this.derivations = CT_DERIVATIONS;
    }
    centralClock(ctx) {
        // Universal waste and used oil take the inherited federal answers.
        // Pharmaceutical waste deliberately does NOT: Connecticut never adopted
        // subpart P, so it falls through to the ordinary tier clocks below
        // whatever the facility's subpart P flag says.
        if (effectiveCtWasteKind(ctx) !== "ORDINARY") {
            return super.centralClock(ctx);
        }
        if (ctx.generatorStatus === "LQG") {
            return CT_LQG_CLOCK;
        }
        if (ctx.generatorStatus === "SQG") {
            // 180 on both values of mustTransport200Miles — see CT_SQG_NOTE.
            return CT_SQG_CLOCK;
        }
        if (ctx.generatorStatus === "VSQG") {
            return CT_CESQG_NO_CLOCK;
        }
        return CT_UNDETERMINED_CLOCK;
    }
    satelliteCaps(ctx) {
        if (effectiveCtWasteKind(ctx) !== "ORDINARY") {
            return super.satelliteCaps(ctx);
        }
        if (ctx.generatorStatus === "VSQG") {
            return CT_CESQG_NO_CAPS;
        }
        // An unknown tier still gets the caps — the more protective default.
        return CT_SATELLITE_CAPS;
    }
    satelliteClock(ctx) {
        if (effectiveCtWasteKind(ctx) !== "ORDINARY") {
            return super.satelliteClock(ctx);
        }
        // 262.34(c) sets no standing clock on satellite accumulation — the only
        // time limit is the breach-triggered 3-day move window, carried on the caps.
        return CT_NO_STANDING_SATELLITE_CLOCK;
    }
    accumulationThresholds(ctx) {
        return {
            thresholds: getCTAccumulationThresholds(ctx.generatorStatus),
            judgedAgainst: getCTReadableGeneratorStatus(ctx.generatorStatus),
        };
    }
}
exports.ConnecticutRules = ConnecticutRules;
//# sourceMappingURL=CT.js.map